// src/oura/merchant/setup/FormStep.tsx
//
// "Your business" on the guided Setup page: the questions about their
// business. Answered, it says so. Not yet, and the backend still takes them
// through the Google Form, it links there and ticks itself off when the
// answers arrive, so nobody is asked the same questions twice. Once sent,
// the answers show under the thanks, read-only, so the merchant can see
// what they told us without asking their account manager. A Google Form
// client's answers show the same way once the backend sends them back.

import { CheckIcon, ExternalLinkIcon } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { cn } from "../../lib/cn";
import { PillLink, TYPE } from "@oura/ui";
import type { MerchantOnboarding, SetupAnswers } from "@service/aeo/onboarding";
import { dayMonth } from "@oura/onboarding/derive";
import { isAnswered, SETUP_QUESTIONS, type SetupQuestion } from "@oura/onboarding/setupQuestions";

const TAP = "max-sm:h-9";

/**
 * The thanks, and under it the answers they sent, open: they came here to
 * see them, whether they answered here or on the Google Form. `answers`
 * null: this backend does not send the filed answers to the merchant yet,
 * and the draft it does send is only Oura's pre-fill, so the thanks stands
 * alone rather than show answers they never gave.
 */
export function FormAnswered({ at, answers }: { at: string | null; answers: SetupAnswers | null }) {
    const when = dayMonth(at);
    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-2xl bg-emerald-50 px-4 py-3">
                <span aria-hidden className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-emerald-500 text-white">
                    <CheckIcon size={15} strokeWidth={3} />
                </span>
                <p className={cn(TYPE.tb, "m-0 text-emerald-900")}>
                    <span className="font-medium">Thanks, we have your answers</span>
                    {when ? `, sent ${when}.` : "."} If something changes, tell your account manager.
                </p>
            </div>
            {answers && (
                <section aria-labelledby="sent-answers" className="space-y-2">
                    <h3 id="sent-answers" className={cn(TYPE.lab, "m-0")}>
                        Your answers
                    </h3>
                    <SentAnswers answers={answers} />
                </section>
            )}
        </div>
    );
}

/** The answers as sent, in the page's question order. Read-only: a change goes through the account manager. */
/**
 * The answers as sent, numbered in the page's question order, whichever way
 * they came in (the Setup page or the Google Form file the same keys), so the
 * two read the same. Read-only: a change goes through the account manager.
 */
function SentAnswers({ answers }: { answers: SetupAnswers }) {
    return (
        <ol className="list-plain m-0 divide-y divide-gray-100 p-0">
            {SETUP_QUESTIONS.map((q, i) => (
                <li key={q.key} className="flex gap-3 py-3.5 first:pt-1 last:pb-0">
                    <span
                        aria-hidden
                        className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-blue-50 text-xs font-semibold tabular-nums text-blue-700"
                    >
                        {i + 1}
                    </span>
                    <div className="min-w-0 flex-1 space-y-1.5 pt-0.5">
                        <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{q.title}</p>
                        <SentAnswer question={q} answers={answers} />
                    </div>
                </li>
            ))}
        </ol>
    );
}

/**
 * An answer's own inline Markdown, read as formatting: Google Form answers
 * come back with `**question**` and the like, and printed raw the asterisks
 * were all they showed (#4446). Bold, italic and code only. Anything else
 * (headings, lists, links) stays text; an image is its alt text, since it
 * has no children to unwrap. A hard break keeps its newline, which the
 * pre-line wrapper shows as one break, same as a soft one. Raw HTML is never
 * rendered (react-markdown's default).
 */
const INLINE = ["p", "strong", "em", "code", "img", "br"];

function AnswerText({ text }: { text: string }) {
    return (
        <ReactMarkdown
            allowedElements={INLINE}
            unwrapDisallowed
            components={{
                // A blank line still starts a new line; one answer is never a stack of paragraphs.
                p: ({ children }) => <span className="block">{children}</span>,
                strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
                code: ({ children }) => <code className="rounded bg-gray-100 px-1 text-[0.9em]">{children}</code>,
                img: ({ alt }) => <>{alt ?? ""}</>,
                br: () => null,
            }}
        >
            {text}
        </ReactMarkdown>
    );
}

/** Short entries ("Teeth whitening") share two columns once there are a few; sentences keep one. */
const SHORT_ENTRY = 40;

function SentAnswer({ question, answers }: { question: SetupQuestion; answers: SetupAnswers }) {
    // The approver shows by name alone: answers filed before the email was asked have none.
    const given = question.key === "approver" ? answers.approver.trim() !== "" : isAnswered(answers, question.key);
    if (!given) return <p className={cn(TYPE.tb, "m-0 text-gray-400")}>Not answered</p>;
    if (question.key === "approver") {
        return (
            <p className={cn(TYPE.tb, "m-0 text-gray-700")}>
                {answers.approver}
                {answers.approverEmail ? <span className="text-gray-500"> · {answers.approverEmail}</span> : null}
            </p>
        );
    }
    const v = answers[question.key];
    if (Array.isArray(v)) {
        const items = v.filter((x) => x.trim());
        const twoColumns = items.length > 4 && items.every((x) => x.length <= SHORT_ENTRY);
        return (
            <ul className={cn("m-0 space-y-1 pl-5 !list-disc marker:text-gray-400", twoColumns && "sm:columns-2 sm:gap-x-8")}>
                {items.map((x) => (
                    <li key={x} className={cn(TYPE.tb, "break-inside-avoid whitespace-pre-line break-words text-gray-700")}>
                        <AnswerText text={x} />
                    </li>
                ))}
            </ul>
        );
    }
    return <div className={cn(TYPE.tb, "m-0 whitespace-pre-line break-words text-gray-700")}><AnswerText text={v ?? ""} /></div>;
}

/** The questions are still on the Google Form: one link to it, opened in a new tab. */
export function FormByLink({ onboarding }: { onboarding: MerchantOnboarding }) {
    const link = onboarding.form?.link ?? null;
    return (
        <div className="space-y-4">
            <p className={cn(TYPE.tb, "m-0 text-gray-600")}>
                A few short questions about what you sell and who buys it. This page ticks itself off when your answers reach us.
            </p>
            {link ? (
                <PillLink variant="primary" href={link} target="_blank" rel="noopener noreferrer" icon={ExternalLinkIcon} className={TAP}>
                    Answer the questions
                </PillLink>
            ) : (
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>Your account manager sends you the questions by email.</p>
            )}
        </div>
    );
}
